import {
  ActorPriceCategoryApprovalType,
  ActorPriceCategoryStatus,
  CountryCodes,
  GenderType,
  Industry,
  LanguageCodes,
  ModuleStatus,
  ModuleType,
  PriceCategoryType,
  ProjectState,
  TextLlm,
  TransactionType,
  VoiceLlm,
  VoiceReactionPacingRhythmTag,
  VoiceReactionTag,
  VoiceReactionVolumeEnergyTag,
  VoiceStyle,
} from "Infrastructure/Api/Api";
import {
  getTranslation,
  Resources,
} from "Infrastructure/Translations/Resources";
import { enumToCodeList } from "Infrastructure/Utils/EnumUtils";
import { EMOTION_META } from "Projects/Utils/spanUtils";
import type { BlOption } from "Shared/Inputs/BlOption";

export function getEnumLabel(
  options: BlOption[],
  value: string | undefined | null,
  fallback = "-",
): string {
  if (!value) return fallback;
  return options.find((o) => o.value === value)?.label ?? value;
}

export const getGenderOptions = (): BlOption[] => [
  { value: GenderType.Male, label: "Muž" },
  { value: GenderType.Female, label: "Žena" },
];

export const getLanguageOptions = (): BlOption[] =>
  enumToCodeList(LanguageCodes, (code) =>
    getTranslation(
      Resources.Common.Languages[
        code as keyof typeof Resources.Common.Languages
      ],
    ),
  );

export const getCountryOptions = (): BlOption[] =>
  enumToCodeList(CountryCodes, (code) =>
    getTranslation(
      Resources.Common.Countries[
        code as keyof typeof Resources.Common.Countries
      ],
    ),
  );

export const getIndustryOptions = (): BlOption[] =>
  enumToCodeList(Industry, (code) =>
    getTranslation(
      Resources.Common.Industries[
        code as keyof typeof Resources.Common.Industries
      ],
    ),
  );

export const getProjectStateOptions = (): BlOption[] => [
  {
    value: ProjectState.InProgress,
    label: getTranslation(Resources.Common.StatusInProgress),
  },
  {
    value: ProjectState.Done,
    label: getTranslation(Resources.Common.StatusDone),
  },
  {
    value: ProjectState.Draft,
    label: getTranslation(Resources.Common.StatusDraft),
  },
  {
    value: ProjectState.Closed,
    label: getTranslation(Resources.Common.StatusClosed),
  },
];

export const getVoiceLlmOptions = (): BlOption[] =>
  enumToCodeList(VoiceLlm, (code) =>
    getTranslation(
      Resources.Common.VoiceLlms[
        code as keyof typeof Resources.Common.VoiceLlms
      ],
    ),
  );

/**
 * Whether the provider can synthesize any language with a single voice (ElevenLabs / Microsoft).
 * Single-language providers (AIVoice) are tied to the voice's own catalog language.
 * Mirrors `VoiceLlmHelper.IsMultilingual` on the backend.
 */
export const isMultilingualVoiceLlm = (voiceLlm: VoiceLlm): boolean => {
  switch (voiceLlm) {
    case VoiceLlm.ElevenLabs_V3:
    case VoiceLlm.ElevenLabs_V2:
    case VoiceLlm.MicrosoftTts:
      return true;
    default:
      return false;
  }
};

export const getVoiceStyleOptions = (): BlOption[] =>
  Object.values(VoiceStyle).map((s) => {
    const emoji = EMOTION_META[s as VoiceStyle]?.emoji;
    const label = getTranslation(
      Resources.Common.VoiceStyles[
        s as keyof typeof Resources.Common.VoiceStyles
      ],
    );
    return {
      value: s,
      label: emoji ? `${emoji} ${label}` : label,
    };
  });

export const getSegmentLanguageOptions = (): BlOption[] => [
  ...Object.values(LanguageCodes).map((code) => ({
    value: code,
    label: getTranslation(
      Resources.Common.Languages[
        code as keyof typeof Resources.Common.Languages
      ],
    ),
  })),
];

export const getModuleTypeName = (type: ModuleType): string => {
  switch (type) {
    case ModuleType.SingleVoiceSpot:
      return getTranslation(Resources.Modules.ModuleTypes.SingleVoiceSpot);
    case ModuleType.MultiVoiceSpot:
      return getTranslation(Resources.Modules.ModuleTypes.MultiVoiceSpot);
    default:
      return type;
  }
};

export const getModuleTypeOptions = (): BlOption[] => [
  {
    value: ModuleType.SingleVoiceSpot,
    label: getTranslation(Resources.Modules.ModuleTypes.SingleVoiceSpot),
  },
  {
    value: ModuleType.MultiVoiceSpot,
    label: getTranslation(Resources.Modules.ModuleTypes.MultiVoiceSpot),
  },
];

export const getModuleStatusOptions = (): BlOption[] => [
  {
    value: ModuleStatus.Active,
    label: getTranslation(Resources.Modules.AvailabilityActive),
  },
  {
    value: ModuleStatus.Inactive,
    label: getTranslation(Resources.Modules.AvailabilityInactive),
  },
];

export const getTextLlmOptions = (): BlOption[] => [
  {
    value: TextLlm.OpenAI_ChatGpt5,
    label: getTranslation(Resources.Modules.TextLlms.OpenAI_ChatGpt5),
  },
];

export const getPriceCategoryTypeOptions = (): BlOption[] => [
  {
    value: PriceCategoryType.PerMinute,
    label: getTranslation(
      Resources.PriceCategories.PriceCategoryTypes.PerMinute,
    ),
  },
  {
    value: PriceCategoryType.PerJob,
    label: getTranslation(Resources.PriceCategories.PriceCategoryTypes.PerJob),
  },
];

export const getActorPriceCategoryStatusOptions = (): BlOption[] => [
  {
    value: ActorPriceCategoryStatus.Allowed,
    label: getTranslation(Resources.Actors.ActorPriceCategoryStatus.Allowed),
  },
  {
    value: ActorPriceCategoryStatus.Exclusive,
    label: getTranslation(Resources.Actors.ActorPriceCategoryStatus.Exclusive),
  },
];

export const getActorPriceCategoryApprovalTypeOptions = (): BlOption[] => [
  {
    value: ActorPriceCategoryApprovalType.Automatic,
    label: getTranslation(
      Resources.Actors.ActorPriceCategoryApprovalType.Automatic,
    ),
  },
  {
    value: ActorPriceCategoryApprovalType.Manual,
    label: getTranslation(
      Resources.Actors.ActorPriceCategoryApprovalType.Manual,
    ),
  },
];

export const getTransactionTypeOptions = (): BlOption[] => [
  { value: "", label: getTranslation(Resources.Common.All) },
  ...Object.values(TransactionType).map((type) => ({
    value: type,
    label: getTranslation(
      Resources.Billing.TransactionTypes[
        type as keyof typeof Resources.Billing.TransactionTypes
      ],
    ),
  })),
];

export const getVoiceReactionOptions = (): BlOption[] =>
  Object.values(VoiceReactionTag).map((v) => ({
    value: v,
    label: getTranslation(
      Resources.JobDetail.Reaction.Values[
        v as keyof typeof Resources.JobDetail.Reaction.Values
      ],
    ),
  }));

export const getVoiceReactionVolumeEnergyOptions = (): BlOption[] =>
  Object.values(VoiceReactionVolumeEnergyTag).map((v) => ({
    value: v,
    label: getTranslation(
      Resources.JobDetail.Reaction.Values[
        v as keyof typeof Resources.JobDetail.Reaction.Values
      ],
    ),
  }));

export const getVoiceReactionPacingRhythmOptions = (): BlOption[] =>
  Object.values(VoiceReactionPacingRhythmTag).map((v) => ({
    value: v,
    label: getTranslation(
      Resources.JobDetail.Reaction.Values[
        v as keyof typeof Resources.JobDetail.Reaction.Values
      ],
    ),
  }));
